import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Download, Phone } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Cuplock', path: '/cuplock' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const handleLinkClick = () => {
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm'
          : 'bg-white/85 backdrop-blur-md border-b border-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={handleLinkClick} className="flex items-center gap-3" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_8a6bf79d-629c-4621-9de5-4520ae741e2c/artifacts/15sezi0u_f665ed4a-3e98-483b-93e6-39a122c5327d.jpeg"
              alt="ARK Scaffolding"
              className="h-14 w-auto"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display font-extrabold text-[#008080] text-lg tracking-tight">ARK</span>
              <span className="font-mono text-[10px] tracking-[0.25em] text-gray-500 uppercase">Scaffolding · Pune</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleLinkClick}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-[#008080]' : 'text-gray-600 hover:text-[#008080]'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-[#DAA520]"></span>
                )}
              </Link>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-2">
            <Link
              to="/brochure"
              data-testid="header-brochure-btn"
              className="inline-flex items-center gap-2 border border-[#008080] text-[#008080] px-4 py-2 text-xs font-mono uppercase tracking-widest font-semibold hover:bg-[#008080] hover:text-white transition rounded"
            >
              <Download size={14} /> Brochure
            </Link>
            <a
              href="tel:+919766601155"
              data-testid="header-call-btn"
              className="ark-btn-primary text-xs px-5 py-2.5"
            >
              <Phone size={14} /> Call Now
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-[#008080] p-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {isMenuOpen && (
          <nav className="lg:hidden pb-5 pt-1 space-y-1 border-t border-gray-200">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleLinkClick}
                className={`block py-3 px-4 text-sm rounded transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#008080] text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-3 flex gap-2">
              <Link to="/brochure" onClick={handleLinkClick} className="flex-1 inline-flex items-center justify-center gap-2 border border-[#008080] text-[#008080] py-2.5 text-xs font-mono uppercase tracking-widest font-semibold rounded">
                <Download size={14} /> Brochure
              </Link>
              <a href="tel:+919766601155" className="ark-btn-primary flex-1 justify-center text-xs">
                <Phone size={14} /> Call
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
